// src/components/roads/page/AiCommandStrip.js
// --- UPDATE: v5.0 — AI/ML command strip for the Road Infrastructure page.
// One glanceable row of model-driven KPIs, each tagged with the model that produced it:
//   Avg RUL (RSF) · Critical-risk roads (RSF, ≤2y) · Unclustered anomalies (iForest → DBSCAN)
//   Live AI events · ML coverage · AI engine health — plus the "Forecast Risk" batch action.
// All values are derived by the page from data it already loads (no extra requests).
import React, { memo, useMemo } from 'react'; // --- UPDATE: + useMemo
import {
    Box, Button, Flex, HStack, Icon, SimpleGrid, Skeleton, Text, Tooltip, useColorModeValue,
    Badge, VStack, // --- UPDATE (insights)
} from '@chakra-ui/react';
import {
    AiGlyph, AiChip, ModelTag, AiNeuralIcon, AiChipIcon, AiAnomalyIcon, RulGaugeIcon,
    AiForecastIcon, MlModelIcon,
} from './AiMlIcons';
import { MotionBox, PulseIndicator, rulBand, ANIM } from './shared';

// --- UPDATE (insights): FlowSense MOW+ Insights in the strip
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
const SEV_SCHEME = { critical: 'red', high: 'orange', medium: 'yellow', low: 'gray', info: 'blue' };
const rankInsights = (list) => [...list]
    .filter((i) => i && i.id !== 'all-clear' && i.id !== 'rule-empty')
    .sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || (b.isLive ? 1 : 0) - (a.isLive ? 1 : 0));

const InsightRow = memo(({ insight, onAction }) => {
    const rowBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.400');
    const scheme = SEV_SCHEME[insight.severity] || 'gray';
    const action = Array.isArray(insight.actions) ? insight.actions[0] : null;
    return (
        <Flex align="center" gap={2} p={2} pl={3} bg={rowBg} borderWidth="1px" borderColor={border}
            borderLeftWidth="4px" borderLeftColor={`${scheme}.400`} borderRadius="lg" minW={0}>
            <Badge colorScheme={scheme} fontSize="9px" borderRadius="full" flexShrink={0}>{insight.isLive ? 'live' : insight.severity}</Badge>
            <Box flex={1} minW={0}>
                <Text fontSize="xs" fontWeight="800" noOfLines={1}>{insight.title}</Text>
                {insight.message && <Text fontSize="10px" color={muted} noOfLines={1}>{insight.message}</Text>}
            </Box>
            {action && onAction && (
                <Button size="xs" variant="outline" colorScheme="purple" flexShrink={0}
                    onClick={() => onAction(action)}>{action.label}</Button>
            )}
        </Flex>
    );
});
InsightRow.displayName = 'AiInsightRow';
// --- END UPDATE

const Kpi = memo(({ icon, gradient, label, value, sub, model, onClick, isActive, isLoading, pulse, tooltip, valueColor }) => {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const activeBorder = useColorModeValue('purple.400', 'purple.300');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const strong = useColorModeValue('gray.800', 'white');

    const body = (
        <MotionBox
            whileHover={onClick ? { y: -3 } : {}}
            whileTap={onClick ? { scale: 0.98 } : {}}
            transition={ANIM.smoothEase}
            h="100%"
        >
            <Flex
                as={onClick ? 'button' : 'div'}
                type={onClick ? 'button' : undefined}
                onClick={onClick}
                aria-pressed={onClick ? !!isActive : undefined}
                w="100%" h="100%" textAlign="left"
                align="center" gap={3} p={3.5}
                bg={bg} borderWidth="1.5px" borderRadius="xl"
                borderColor={isActive ? activeBorder : border}
                boxShadow={isActive ? '0 0 0 3px rgba(128,90,213,0.18)' : 'sm'}
                cursor={onClick ? 'pointer' : 'default'}
                transition="all 0.2s"
                _hover={onClick ? { borderColor: 'purple.300', boxShadow: 'md' } : {}}
                _focusVisible={{ outline: '2px solid', outlineColor: 'purple.400' }}
            >
                <AiGlyph icon={icon} gradient={gradient} size={10} iconSize={5} pulse={pulse} />
                <Box minW={0} flex={1}>
                    <HStack spacing={1.5} mb={0.5}>
                        <Text fontSize="10px" fontWeight="800" color={labelColor}
                            textTransform="uppercase" letterSpacing="wider" noOfLines={1}>
                            {label}
                        </Text>
                        {model && <ModelTag model={model} />}
                    </HStack>
                    {isLoading ? (
                        <Skeleton h="22px" w="56px" borderRadius="md" />
                    ) : (
                        <Text fontSize="xl" fontWeight="900" lineHeight="1.1" color={valueColor || strong}>
                            {value}
                        </Text>
                    )}
                    {sub && <Text fontSize="10px" color={labelColor} noOfLines={1}>{sub}</Text>}
                </Box>
            </Flex>
        </MotionBox>
    );
    return tooltip ? <Tooltip label={tooltip} hasArrow openDelay={300}>{body}</Tooltip> : body;
});
Kpi.displayName = 'AiKpi';

/**
 * @param {object}   props
 * @param {number|string} props.averageRUL
 * @param {number}   props.criticalRiskCount   roads on the page with RUL ≤ 2y
 * @param {number}   props.scoredCount         roads on the page with an RUL score
 * @param {number}   props.pageCount           roads on the page
 * @param {number}   props.unclusteredCount
 * @param {number}   props.liveEventsCount
 * @param {number}   props.totalUnmappedRoads
 * @param {string}   props.aiCircuit           'OPEN' | 'CLOSED' | 'HALF_OPEN'
 * @param {string}   props.aiStatus            idle | thinking | success | error
 * @param {boolean}  props.isLoading
 * @param {boolean}  props.isForecasting
 * @param {boolean}  props.criticalFilterActive
 * @param {Function} props.onForecast
 * @param {Function} props.onToggleCriticalFilter
 * @param {Function} props.onOpenMap
 * @param {Function} props.onShowStarved
 */
const AiCommandStrip = ({
    averageRUL, criticalRiskCount = 0, scoredCount = 0, pageCount = 0, unclusteredCount = 0,
    liveEventsCount = 0, totalUnmappedRoads = 0, aiCircuit = 'CLOSED', aiStatus = 'idle',
    isLoading, isForecasting, criticalFilterActive, onForecast, onToggleCriticalFilter,
    onOpenMap, onShowStarved,
    // --- UPDATE (insights): FlowSense MOW+ Insights feed (batch + live) and its action handler
    insights = [], insightsLoading = false, onInsightAction, onOpenInsights,
    // --- END UPDATE
}) => {
    // --- UPDATE (insights)
    const ranked = useMemo(() => rankInsights(Array.isArray(insights) ? insights : []), [insights]);
    const urgentCount = ranked.filter((i) => i.severity === 'critical' || i.severity === 'high').length;
    const allClear = !ranked.length && (insights || []).some((i) => i?.id === 'all-clear');
    // --- END UPDATE
    const shellBg = useColorModeValue(
        'linear(135deg, purple.50 0%, white 45%, blue.50 100%)',
        'linear(135deg, rgba(128,90,213,0.16) 0%, gray.800 50%, rgba(49,130,206,0.14) 100%)'
    );
    const shellBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
    const titleColor = useColorModeValue('gray.800', 'white');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const avg = Number(averageRUL);
    const band = avg > 0 ? rulBand(avg) : null;
    const coverage = pageCount ? Math.round((scoredCount / pageCount) * 100) : 0;
    const circuitOpen = String(aiCircuit).toUpperCase() === 'OPEN';
    const engineLabel = circuitOpen ? 'Degraded' : aiStatus === 'error' ? 'Error' : aiStatus === 'thinking' ? 'Analyzing' : 'Online';
    const engineColor = circuitOpen || aiStatus === 'error' ? 'red.400' : aiStatus === 'thinking' ? 'blue.400' : 'green.400';

    return (
        <Box
            bgGradient={shellBg} borderWidth="1px" borderColor={shellBorder}
            borderRadius="2xl" p={{ base: 3, md: 4 }} mb={6} position="relative" overflow="hidden"
            role="region" aria-label="AI and machine learning overview"
        >
            <Box position="absolute" top="-40px" right="-40px" w="160px" h="160px"
                bg="purple.300" opacity={0.12} borderRadius="full" filter="blur(40px)" pointerEvents="none" />

            <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
                <HStack spacing={3}>
                    <AiGlyph icon={AiNeuralIcon} size={9} iconSize={5} pulse={liveEventsCount > 0} />
                    <Box>
                        <HStack spacing={2}>
                            <Text fontWeight="900" fontSize="sm" color={titleColor}>AI / ML Command Strip</Text>
                            <AiChip kind="ai" size="xs" />
                            <AiChip kind="ml" size="xs" />
                        </HStack>
                        <HStack spacing={1.5} mt={0.5}>
                            <PulseIndicator color={circuitOpen ? 'red' : 'green'} size={2} />
                            <Text fontSize="xs" color={muted}>
                                Predictive maintenance engine · {engineLabel}
                            </Text>
                        </HStack>
                    </Box>
                </HStack>
                <Tooltip label="Run Random Survival Forest prediction against all active road segments" hasArrow>
                    <Button
                        size="sm" borderRadius="xl" color="white"
                        bgGradient="linear(to-r, purple.500, pink.500)"
                        _hover={{ bgGradient: 'linear(to-r, purple.600, pink.600)', transform: 'translateY(-1px)', boxShadow: '0 8px 20px rgba(128,90,213,0.35)' }}
                        _active={{ bgGradient: 'linear(to-r, purple.700, pink.700)' }}
                        leftIcon={<Icon as={AiForecastIcon} boxSize={4} />}
                        onClick={onForecast} isLoading={isForecasting} loadingText="Forecasting…"
                        isDisabled={circuitOpen}
                    >
                        Forecast Risk
                    </Button>
                </Tooltip>
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 4, xl: 7 }} spacing={3}>{/* --- UPDATE: + Insights KPI */}
                <Kpi
                    icon={RulGaugeIcon} model="rsf"
                    gradient="linear(135deg, purple.500, blue.500)"
                    label="Avg RUL" isLoading={isLoading && !pageCount}
                    value={avg > 0 ? `${avg.toFixed(1)}y` : '—'}
                    valueColor={band ? `${band.color}.500` : undefined}
                    sub={band ? `${band.label} network health` : 'No predictions yet'}
                    tooltip="Average predicted remaining useful life of scored roads on this page"
                />
                <Kpi
                    icon={AiForecastIcon} model="rsf"
                    gradient="linear(135deg, red.400, pink.500)"
                    label="Critical risk" isLoading={isLoading && !pageCount}
                    value={criticalRiskCount}
                    valueColor={criticalRiskCount > 0 ? 'red.500' : undefined}
                    sub={criticalFilterActive ? 'Filter active — click to clear' : 'RUL ≤ 2 years · click to filter'}
                    onClick={onToggleCriticalFilter} isActive={criticalFilterActive}
                    pulse={criticalRiskCount > 0}
                />
                <Kpi
                    icon={AiAnomalyIcon} model="iforest"
                    gradient="linear(135deg, orange.400, red.500)"
                    label="Anomalies" isLoading={isLoading && !pageCount}
                    value={unclusteredCount}
                    sub="Unclustered · open GIS to cluster"
                    onClick={onOpenMap}
                />
                <Kpi
                    icon={AiNeuralIcon} model="insights"
                    gradient="linear(135deg, pink.500, purple.500)"
                    label="Live AI events" value={liveEventsCount}
                    sub={liveEventsCount ? 'Streaming via WebSocket' : 'Listening…'}
                    pulse={liveEventsCount > 0}
                />
                <Kpi
                    icon={MlModelIcon}
                    gradient="linear(135deg, blue.500, cyan.500)"
                    label="ML coverage" isLoading={isLoading && !pageCount}
                    value={`${coverage}%`}
                    sub={`${totalUnmappedRoads} ML-starved · click to view`}
                    onClick={onShowStarved}
                    tooltip="Share of roads on this page with an RUL prediction. ML-starved roads lack geometry/feature data."
                />
                {/* --- UPDATE (insights) */}
                <Kpi
                    icon={AiNeuralIcon} model="insights"
                    gradient={urgentCount ? 'linear(135deg, red.400, purple.500)' : 'linear(135deg, purple.500, teal.400)'}
                    label="AI insights" isLoading={insightsLoading && !ranked.length}
                    value={ranked.length}
                    valueColor={urgentCount ? 'red.500' : undefined}
                    sub={urgentCount ? `${urgentCount} urgent · click to view` : allClear ? 'All checks clear' : ranked.length ? 'Click to view' : 'Waiting for data'}
                    onClick={onOpenInsights}
                    pulse={urgentCount > 0}
                    tooltip="FlowSense MOW+ Insights: remaining life, condition, data gaps, inspections, Drive Mode, work orders, public reports and flood risk"
                />
                {/* --- END UPDATE */}
                <Kpi
                    icon={AiChipIcon}
                    gradient={circuitOpen ? 'linear(135deg, red.500, orange.400)' : 'linear(135deg, green.400, teal.500)'}
                    label="AI engine" value={engineLabel} valueColor={engineColor}
                    sub={`Circuit ${String(aiCircuit).toUpperCase()}`}
                    tooltip="Road AI engine circuit breaker. When OPEN, cached predictions are served."
                />
            </SimpleGrid>

            {/* --- UPDATE (insights): the most urgent FlowSense MOW+ Insights, one click to act */}
            {ranked.length > 0 && (
                <Box mt={3}>
                    <HStack justify="space-between" mb={1.5}>
                        <Text fontSize="10px" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider">
                            Top insights
                        </Text>
                        {onOpenInsights && ranked.length > 3 && (
                            <Button size="xs" variant="link" colorScheme="purple" onClick={onOpenInsights}>
                                View all {ranked.length}
                            </Button>
                        )}
                    </HStack>
                    <VStack spacing={1.5} align="stretch">
                        {ranked.slice(0, 3).map((i) => <InsightRow key={i.id} insight={i} onAction={onInsightAction} />)}
                    </VStack>
                </Box>
            )}
            {/* --- END UPDATE */}
        </Box>
    );
};

export default memo(AiCommandStrip);
